import Logo from '@/pages/home/logo';
import { BottomSectionContainer, PageContainer, MainScrollArea } from './component';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import PlaceHolderIcon from './icon/placeholder.svg?react';
import InspectIcon from './icon/inspect.svg?react';
import TraceIcon from './icon/trace.svg?react';
import ViewIcon from './icon/view.svg?react';
import PreloadIcon1 from './icon/preload_icon1.svg?react';
import PreloadIcon2 from './icon/preload_icon2.svg?react';
import PreloadIcon3 from './icon/preload_icon3.svg?react';
import PreloadIcon4 from './icon/preload_icon4.svg?react';
import { useAtom } from 'jotai';
import { onboardingAtom } from '@/pages/onboarding/state';
import { useNavigate } from 'react-router-dom';

const ContentWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  flexWrap: 'wrap',
});

const LeftPanel = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  flexShrink: 1,
  flexGrow: 1,
  flexBasis: 0,
});

const SectionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const SectionTitle = styled('div', {
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StepList = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  borderRadius: '8px',
  padding: '24px',
  border: `1px solid ${$theme.colors['border.tertiary']}`,
}));

const StepItem = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '16px',
});

const StepIcon = styled('div', {
  flexShrink: 0,
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(232, 132, 44, 0.10)',
});

const StepContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const StepTitle = styled('div', {
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
});

const StepDesc = styled('div', ({ $theme }) => ({
  lineHeight: '24px',
  color: $theme.colors['text.secondary'],
}));

const PreloadList = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
});

const PreloadItem = styled('div', ({ $theme }) => ({
  flex: 1,
  borderRadius: '8px',
  padding: '16px 12px 24px',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${$theme.colors['border.tertiary']}`,
}));

const PreloadIcon = styled('div', {
  flexShrink: 0,
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '8px',
});

const PreloadLabel = styled('div', {
  fontWeight: 500,
  lineHeight: '24px',
  fontSize: '14px',
});

const PreloadDesc = styled('div', ({ $theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: $theme.colors['text.secondary'],
}));

const SchemaPreview = styled('div', ({ $theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  border: `1px solid ${$theme.colors['border.tertiary']}`,
  background: $theme.colors['background.secondary'],
}));

export default function ViewSampleData() {
  const [css, theme] = useStyletron();
  const [{ workspace }] = useAtom(onboardingAtom);
  const navigate = useNavigate();
  return (
    <PageContainer>
      <MainScrollArea
        $style={{
          backgroundColor: theme.colors['background.primary'],
          alignItems: 'center',
          padding: '24px',
        }}
      >
        <Logo alwaysShow />
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          })}
        >
          <div className={css({ fontSize: '32px', fontWeight: 600, lineHeight: '40px' })}>
            Ready to Trace a Suspicious Transaction?
          </div>
          <div className={css({ color: theme.colors['text.secondary'] })}>
            {`Explore our preloaded fraud detection graph with sample data. We'll guide you through analyzing transactions and uncovering suspicious patterns.`}{' '}
          </div>
        </div>
        <ContentWrapper
          $style={{
            flexGrow: 1,
            alignSelf: 'stretch',
          }}
        >
          <LeftPanel>
            <SectionContainer>
              <SectionTitle>
                <PlaceHolderIcon />
                <span>Your Guided Experience</span>
              </SectionTitle>
              <StepList>
                <StepItem>
                  <StepIcon>
                    <InspectIcon />
                  </StepIcon>
                  <StepContent>
                    <StepTitle>Inspect account attributes and linked transactions</StepTitle>
                    <StepDesc>Explore how accounts connect to transactions, devices, and other entities</StepDesc>
                  </StepContent>
                </StepItem>
                <StepItem>
                  <StepIcon>
                    <TraceIcon />
                  </StepIcon>
                  <StepContent>
                    <StepTitle>Trace connections to detect potential fraud</StepTitle>
                    <StepDesc>Follow suspicious patterns through the network to identify fraud rings</StepDesc>
                  </StepContent>
                </StepItem>
                <StepItem>
                  <StepIcon>
                    <ViewIcon />
                  </StepIcon>
                  <StepContent>
                    <StepTitle>View visual insights from the dashboard</StepTitle>
                    <StepDesc>Analyze fraud patterns with interactive visualizations and metrics</StepDesc>
                  </StepContent>
                </StepItem>
              </StepList>
            </SectionContainer>
            <SectionContainer>
              <SectionTitle>
                <PlaceHolderIcon />
                <span>What&rsquo;s Preloaded</span>
              </SectionTitle>
              <PreloadList>
                <PreloadItem>
                  <PreloadIcon $style={{ background: '#DBEAFE' }}>
                    <PreloadIcon1 />
                  </PreloadIcon>
                  <PreloadLabel>Graph Schema</PreloadLabel>
                  <PreloadDesc>20+ entity types</PreloadDesc>
                </PreloadItem>
                <PreloadItem>
                  <PreloadIcon $style={{ background: '#DCFCE7' }}>
                    <PreloadIcon2 />
                  </PreloadIcon>
                  <PreloadLabel>Transactions</PreloadLabel>
                  <PreloadDesc>800,000+ records</PreloadDesc>
                </PreloadItem>
                <PreloadItem>
                  <PreloadIcon $style={{ background: '#F3E8FF' }}>
                    <PreloadIcon3 />
                  </PreloadIcon>
                  <PreloadLabel>Installed Queries</PreloadLabel>
                  <PreloadDesc>PageRank & More</PreloadDesc>
                </PreloadItem>
                <PreloadItem>
                  <PreloadIcon $style={{ background: '#FEF3C7' }}>
                    <PreloadIcon4 />
                  </PreloadIcon>
                  <PreloadLabel>Dashboards</PreloadLabel>
                  <PreloadDesc>Visual insights</PreloadDesc>
                </PreloadItem>
              </PreloadList>
            </SectionContainer>
          </LeftPanel>
          <SectionContainer
            $style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              minWidth: '400px',
              minHeight: '400px',
            }}
          >
            <SectionTitle>
              <PlaceHolderIcon />
              <span>Graph Schema Preview</span>
            </SectionTitle>
            <SchemaPreview />
          </SectionContainer>
        </ContentWrapper>
      </MainScrollArea>
      <BottomSectionContainer
        $style={{
          background: theme.colors['background.secondary'],
          justifyContent: 'space-between',
        }}
      >
        <div
          className={css({
            fontSize: '16px',
            color: theme.colors['text.secondary'],
          })}
        >
          Your workspace is ready with all sample data loaded.{' '}
        </div>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
          })}
        >
          {/* <Button kind="secondary" size="large" overrides={{ BaseButton: { style: { marginRight: '16px' } } }}>
            Skip Tutorial
          </Button> */}
          <Button
            kind="primary"
            size="large"
            onClick={() => navigate(`/groups/${workspace?.workgroup_id}`, { replace: true })}
          >
            Let&rsquo;s Begin &rarr;
          </Button>
        </div>
      </BottomSectionContainer>
    </PageContainer>
  );
}
